import { ethers } from 'ethers';

export interface RPCErrorOptions {
  maxRetries?: number;
  retryDelay?: number;
  backoffMultiplier?: number;
}

export interface RPCErrorResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  retries?: number;
}

/**
 * Utility class for handling RPC errors with retry logic
 */
export class RPCErrorHandler {
  private static defaultOptions: Required<RPCErrorOptions> = {
    maxRetries: 5,
    retryDelay: 500,
    backoffMultiplier: 1.5
  };

  /**
   * Execute a contract call with retry logic for RPC errors
   */
  static async executeWithRetry<T>(
    operation: () => Promise<T>,
    options: RPCErrorOptions = {}
  ): Promise<RPCErrorResult<T>> {
    const config = { ...this.defaultOptions, ...options };
    let lastError: Error | null = null;
    
    for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
      try {
        const result = await operation();
        return {
          success: true,
          data: result,
          retries: attempt
        };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        
        // Check if this is an RPC error that should be retried
        if (this.shouldRetry(lastError) && attempt < config.maxRetries) {
          const delay = config.retryDelay * Math.pow(config.backoffMultiplier, attempt);
          console.warn(`RPC call failed, retrying in ${delay}ms (attempt ${attempt + 1}/${config.maxRetries})`, {
            error: lastError.message,
            attempt: attempt + 1
          });
          
          await this.delay(delay);
          continue;
        }
        
        // Don't retry or max retries reached
        break;
      }
    }
    
    return {
      success: false,
      error: lastError?.message || 'Unknown RPC error',
      retries: config.maxRetries
    };
  }

  /**
   * Check if an error should trigger a retry
   */
  private static shouldRetry(error: Error): boolean {
    const retryableErrors = [
      'Internal JSON-RPC error',
      'missing revert data',
      'CALL_EXCEPTION',
      'NETWORK_ERROR',
      'TIMEOUT',
      'SERVER_ERROR',
      'UNKNOWN_ERROR',
      'Failed to fetch',
      'circuit breaker',
      'Execution prevented because the circuit breaker is open',
      'invalid block range params'
    ];
    
    // Don't retry network configuration errors
    const nonRetryableErrors = [
      'wrong network',
      'invalid chain id',
      'contract not deployed',
      'invalid address'
    ];
    
    if (nonRetryableErrors.some(nonRetryableError => 
      error.message.toLowerCase().includes(nonRetryableError)
    )) {
      return false;
    }
    
    return retryableErrors.some(retryableError => 
      error.message.includes(retryableError) || 
      error.message.toLowerCase().includes('rpc')
    );
  }

  /**
   * Delay execution for specified milliseconds
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Enhanced error logging for RPC errors
   */
  static logRPCError(operation: string, error: Error, context?: Record<string, any>): void {
    console.error(`RPC Error in ${operation}:`, {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      context
    });
    
    // Additional logging for specific error types
    if (error.message.includes('missing revert data')) {
      console.error('This error typically occurs when:', {
        reason: 'Contract call failed but no revert reason was provided',
        possibleCauses: [
          'Contract state is invalid',
          'Insufficient gas',
          'Contract not deployed at address',
          'ABI mismatch',
          'Wrong network connection'
        ],
        suggestions: [
          'Verify contract address is correct',
          'Check if contract is deployed',
          'Verify ABI matches deployed contract',
          'Ensure sufficient gas for the call',
          'Check if connected to correct network (Swarm devnet)'
        ]
      });
    }

    if (error.message.includes('invalid block range params')) {
      console.error('This error typically occurs when:', {
        reason: 'Event filter parameters are invalid',
        possibleCauses: [
          'Wrong network connection',
          'Invalid block numbers',
          'Contract not deployed on current network'
        ],
        suggestions: [
          'Check if connected to correct network (Swarm devnet)',
          'Verify contract addresses match current network',
          'Ensure block numbers are valid for current network'
        ]
      });
    }

    if (error.message.includes('Failed to fetch')) {
      console.error('This error typically occurs when:', {
        reason: 'Network request failed',
        possibleCauses: [
          'Wrong network in MetaMask',
          'RPC endpoint not accessible',
          'Network connectivity issues',
          'MetaMask not connected to correct network'
        ],
        suggestions: [
          'Check if MetaMask is connected to Swarm devnet',
          'Verify RPC endpoint is accessible',
          'Try switching networks in MetaMask',
          'Refresh the page and reconnect wallet'
        ]
      });
    }
  }


}

/**
 * Hook for handling RPC errors in React components
 */
export const useRPCErrorHandling = () => {
  const handleRPCError = (error: Error, operation: string, context?: Record<string, any>) => {
    RPCErrorHandler.logRPCError(operation, error, context);
    
    // Return user-friendly error message
    if (error.message.includes('missing revert data')) {
      return 'Transaction failed - please check your connection and try again';
    }
    
    if (error.message.includes('Internal JSON-RPC error')) {
      return 'Network error - please check your connection and try again';
    }
    
    return error.message || 'An unexpected error occurred';
  };

  return { handleRPCError };
};
